import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { call, put, takeEvery } from 'redux-saga/effects';
import {
  CompanyDetails,
  registerCompany,
  sendOTP,
  sendSignInOTP,
  setCompanyDetails,
  setError,
  setLoading,
  setSendOTP,
  setSignInSendOTPFailure,
  setSignInSendOTPSuccess,
  setStage,
  setSuccess,
  setUpdateAgencySuccess,
  setValidOTP,
  updateTempCompany,
  verifyOTP,
} from 'redux/slice/agency-onbording/SignUp.slice';
import {
  AgencyResponse,
  OTPResponse,
  SendOTPRequest as SendOTP,
  VerifyOTPRequest as VerifyOTP,
  VerifyOTPResponse,
} from 'redux/types/agency-onboarding/Agency.types';
import {
  ApiError,
  SagaIterator,
  SignInOTPResponse,
  UpdateAgencyResponse,
} from 'redux/types/agency-onboarding/ApiResponse.types';
import { agencyOnboarding } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

function* handleSendOTP({
  payload,
}: PayloadAction<SendOTP>): SagaIterator<void> {
  const { phoneNumber } = payload;
  try {
    yield put(setLoading(true));
    const response: OTPResponse = yield call(
      HTTPClient.PostRequest,
      agencyOnboarding.SEND_OTP,
      payload
    );
    if (response.data.status === 200) {
      // Convert string 'true'/'false' to boolean
      const isSignUpNeeded = response.data.data.isSignUpNeeded === 'true';
      yield put(
        setSendOTP({
          phoneNumber,
          isSignUpNeeded,
          message: response.data.message,
        })
      );

      // If isSignUpNeeded is false and stage is provided, update the stage
      if (!isSignUpNeeded && response.data.data.stage) {
        yield put(setStage(parseInt(response.data.data.stage, 10)));
      }
    } else {
      yield put(setError('Failed to send OTP. Please try again.'));
    }
  } catch (error: unknown) {
    const typedError = error as ApiError;
    // Check if it's a server error (500)
    if (typedError.response && typedError.response.status === 500) {
      // Check for specific error message about null agency
      if (
        typedError.response.data &&
        typedError.response.data.errors &&
        typedError.response.data.errors.length > 0 &&
        typedError.response.data.errors[0].message &&
        typedError.response.data.errors[0].message.includes(
          'yucollectAgency" is null'
        )
      ) {
        yield put(
          setError(
            'Unable to send OTP. Your account information could not be found. Please contact support.'
          )
        );
      } else {
        yield put(setError('Server error occurred. Please try again later.'));
      }
    } else {
      yield put(
        setError(
          typedError.message ||
            'An error occurred while sending OTP. Please try again.'
        )
      );
    }
  } finally {
    yield put(setLoading(false));
  }
}

function* handleSendSignInOTP({
  payload,
}: PayloadAction<SendOTP>): SagaIterator<void> {
  const { phoneNumber } = payload;
  try {
    yield put(setLoading(true));
    const response: SignInOTPResponse = yield call(
      HTTPClient.PostRequest,
      agencyOnboarding.SEND_SIGNIN_OTP,
      payload
    );
    if (response.status === 200) {
      if (
        response.data.isOTPSent === 'false' &&
        response.data.isSignUpNeeded === 'true'
      ) {
        yield put(setSignInSendOTPFailure({ phoneNumber }));
      } else if (
        response.data.isOTPSent === 'true' &&
        response.data.isSignUpNeeded === 'false'
      ) {
        yield put(setSignInSendOTPSuccess({ phoneNumber }));
      }
    } else {
      yield put(setError('Failed to send OTP. Please try again.'));
    }
  } catch (error: unknown) {
    const typedError = error as ApiError;
    // Check if it's a server error (500)
    if (typedError.response && typedError.response.status === 500) {
      // Check for specific error message about null agency
      if (
        typedError.response.data &&
        typedError.response.data.errors &&
        typedError.response.data.errors.length > 0 &&
        typedError.response.data.errors[0].message &&
        typedError.response.data.errors[0].message.includes(
          'yucollectAgency" is null'
        )
      ) {
        yield put(
          setError(
            'Unable to send OTP. Your account information could not be found. Please contact support.'
          )
        );
      } else {
        yield put(setError('Server error occurred. Please try again later.'));
      }
    } else {
      yield put(
        setError(
          typedError.message ||
            'An error occurred while sending OTP. Please try again.'
        )
      );
    }
  } finally {
    yield put(setLoading(false));
  }
}

function* handleVerifyOTP({
  payload,
}: PayloadAction<VerifyOTP>): SagaIterator<void> {
  const { phoneNumber, otp } = payload;
  try {
    yield put(setLoading(true));
    const response: VerifyOTPResponse = yield call(
      HTTPClient.PostRequest,
      agencyOnboarding.VERIFY_OTP,
      payload
    );

    if (response.data.status === 200 && response.data.data) {
      // OTP verification successful
      if (response.data.data.id) {
        // Set the stage from the response
        if (response.data.data.stage) {
          yield put(setStage(response.data.data.stage));
        }

        // Convert the response data to match CompanyDetails interface
        const companyDetails: CompanyDetails = {
          id: response.data.data.id || null,
          name:
            response.data.data.name !== undefined
              ? response.data.data.name
              : null,
          companyName:
            response.data.data.companyName !== undefined
              ? response.data.data.companyName
              : null,
          designation:
            response.data.data.designation !== undefined
              ? response.data.data.designation
              : null,
          emailId:
            response.data.data.emailId !== undefined
              ? response.data.data.emailId
              : null,
          pincode:
            response.data.data.pincode !== undefined
              ? response.data.data.pincode
              : null,
          address:
            response.data.data.address !== undefined
              ? response.data.data.address
              : null,
          area:
            response.data.data.area !== undefined
              ? response.data.data.area
              : null,
          city:
            response.data.data.city !== undefined
              ? response.data.data.city
              : null,
        };

        // Set company details and valid OTP
        yield put(setCompanyDetails(companyDetails));
        yield put(setValidOTP(companyDetails));

        // Store the complete agency object in local storage
        localStorage.setItem('tempEntityId', response.data.data.id);
        const mergedData = { ...response.data.data };

        // Fetch V2 product bucket config from GET API
        try {
          const pbResponse = yield call(
            HTTPClient.GetRequest,
            agencyOnboarding.GET_AGENCY_PRODUCT_BUCKET(response.data.data.id)
          );
          if (pbResponse?.data?.data?.productBucketConfig) {
            mergedData.productBucketList =
              pbResponse.data.data.productBucketConfig.map((item: any) => ({
                productId: item.productId,
                bucketIds: item.bucketIds,
                allocatorIds: item.allocatorIds,
                is_expert: item.isExpert ?? false,
              }));
          }
        } catch {
          // GET API failed — product buckets won't be prefilled
        }

        localStorage.setItem('agencyData', JSON.stringify(mergedData));
      } else {
        yield put(
          setError('Missing account information in response. Please try again.')
        );
      }
    } else {
      // OTP verification failed
      yield put(setError('Invalid OTP or OTP has expired. Please try again.'));
    }
  } catch (error: unknown) {
    const typedError = error as ApiError;
    // Check if it's a server error (500)
    if (typedError.response && typedError.response.status === 500) {
      // Check for specific error message about null agency
      if (
        typedError.response.data &&
        typedError.response.data.errors &&
        typedError.response.data.errors.length > 0 &&
        typedError.response.data.errors[0].message &&
        typedError.response.data.errors[0].message.includes(
          'yucollectAgency" is null'
        )
      ) {
        yield put(
          setError(
            'Unable to verify OTP. Your account information could not be found. Please contact support.'
          )
        );
      } else {
        yield put(
          setError(
            'Server error occurred while verifying OTP. Please try again later.'
          )
        );
      }
    } else {
      if (typedError.response?.data?.message) {
        yield put(setError(typedError.response.data.message));
      } else {
        yield put(
          setError(
            'An unexpected error occurred during registration. Please try again.'
          )
        );
      }
    }
  } finally {
    yield put(setLoading(false));
  }
}

function* registerCompanyFnc({
  payload,
}: PayloadAction<CompanyDetails>): SagaIterator<void> {
  try {
    yield put(setLoading(true));
    const response: AgencyResponse = yield call(
      HTTPClient.PostRequest,
      agencyOnboarding.REGISTER_COMPANY,
      payload
    );

    if (response.data.status === 200 && response.data.data.id) {
      yield put(setSuccess(true));
      const existingData = JSON.parse(
        localStorage.getItem('agencyData') || '{}'
      );
      const mergedData = { ...existingData, ...response.data.data };
      if (
        !response.data.data.productBucketList &&
        existingData.productBucketList
      ) {
        mergedData.productBucketList = existingData.productBucketList;
      }
      localStorage.setItem('agencyData', JSON.stringify(mergedData));
    } else {
      yield put(setError('Something went wrong'));
    }
  } catch (error: unknown) {
    const typedError = error as ApiError;
    yield put(setError(typedError.message || 'An error occurred'));
  } finally {
    yield put(setLoading(false));
  }
}

function* handleUpdateCompany({
  payload,
}: PayloadAction<CompanyDetails>): SagaIterator<void> {
  try {
    yield put(setLoading(true));
    const response: UpdateAgencyResponse = yield call(
      HTTPClient.PostRequest,
      agencyOnboarding.UPDATE_TEMP_AGENCY,
      payload
    );

    if (response.status === 200 && response.data.data) {
      const existingData = JSON.parse(
        localStorage.getItem('agencyData') || '{}'
      );
      message.success('Details updated successfully');
      yield put(setUpdateAgencySuccess(true));
      const mergedData = { ...existingData, ...response.data.data };
      if (
        !response.data.data.productBucketList &&
        existingData.productBucketList
      ) {
        mergedData.productBucketList = existingData.productBucketList;
      }
      localStorage.setItem('agencyData', JSON.stringify(mergedData));
    } else {
      yield put(setError('Failed to update!'));
    }
  } catch (error: unknown) {
    const typedError = error as ApiError;
    yield put(setError(typedError.message || 'An error occurred'));
  } finally {
    yield put(setLoading(false));
  }
}

function* watchSendOTP() {
  yield takeEvery(sendOTP.type, handleSendOTP);
  yield takeEvery(sendSignInOTP.type, handleSendSignInOTP);
  yield takeEvery(verifyOTP.type, handleVerifyOTP);
  yield takeEvery(registerCompany.type, registerCompanyFnc);
  yield takeEvery(updateTempCompany.type, handleUpdateCompany);
}

export default watchSendOTP;
